import { useState } from "react";
import { IconChevronDown, IconDownload, IconMessageQuestion, IconShieldCheck, IconInfoCircle } from "@tabler/icons-react";
import s from "../partnerPortal.module.css";
import r from "./ResultReport.module.css";
import { ModalClose } from "./shared";
import {
  BAND_MEANING,
  BAND_ORDER,
  FIT_GUIDANCE,
  ONE_INPUT_NOTE,
  benchmarkLine,
  methodLine,
  partnerIntegrityLines,
} from "./reportData";

const BAND_CLASS = { Strong: r.bandStrong, Proficient: r.bandProficient, Developing: r.bandDeveloping };
const FIT_CLASS = { "Strong fit": r.fitStrong, "Potential fit": r.fitPotential, "Limited evidence": r.fitLimited };

function Section({ title, icon, children }) {
  return (
    <section className={r.section}>
      <div className={r.sectionHead}>
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </section>
  );
}

function CompetencyRow({ c, open, onToggle }) {
  return (
    <div className={r.comp}>
      <button type="button" className={r.compHead} onClick={onToggle} aria-expanded={open}>
        <span className={r.compName}>{c.name}</span>
        <span className={`${r.band} ${BAND_CLASS[c.band] || ""}`}>{c.band}</span>
        <span className={r.compScore}>{c.score}%</span>
        <IconChevronDown size={15} className={`${r.chev} ${open ? r.chevOpen : ""}`} />
      </button>
      <div className={r.bar} aria-hidden="true">
        <div className={`${r.barFill} ${BAND_CLASS[c.band] || ""}`} style={{ width: `${Math.max(c.score, 2)}%` }} />
      </div>
      {open && (
        <div className={r.compBody}>
          {c.justification && (
            <p className={r.p}>
              <b>Why this score:</b> {c.justification}
            </p>
          )}
          {c.evidence.length > 0 && (
            <div className={r.quotes}>
              {c.evidence.map((q, i) => (
                <blockquote key={i} className={r.quote}>
                  “{q}”
                </blockquote>
              ))}
            </div>
          )}
          {c.question && (
            <p className={r.muted}>
              <b>Scenario:</b> {c.question}
            </p>
          )}
          <div className={r.answer}>
            <div className={r.answerLabel}>Candidate's answer</div>
            <div className={r.answerText}>{c.answer || "No answer given."}</div>
          </div>
          {c.weight > 1 && <p className={r.muted}>Weighted ×{c.weight} in the overall score.</p>}
        </div>
      )}
    </div>
  );
}

export default function ResultReport({ report, brandName, onClose, onDownload, downloading }) {
  const [openIds, setOpenIds] = useState(() => new Set());
  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOpen = report.competencies?.length > 0 && openIds.size === report.competencies.length;
  const toggleAll = () => setOpenIds(allOpen ? new Set() : new Set(report.competencies.map((c) => c.id)));

  const sjt = report.kind === "sjt";
  const scored = !sjt || report.status === "scored";

  return (
    <>
      <div className={`${s.modalHead} ${r.head}`}>
        <ModalClose onClose={onClose} />
        <div className={r.headText}>
          {brandName && <div className={r.eyebrow}>{brandName} · Assessment report</div>}
          <div className={`${s.modalTitle} ${s.modalTitleLg}`}>{report.cand}</div>
          <div className={s.modalSub}>
            {[report.role, `completed ${report.date}`, report.email].filter(Boolean).join(" · ")}
            {report.timedOut ? " · time ran out, scored on saved answers" : ""}
          </div>
        </div>
        <div className={s.scoreBadge}>
          <div className={s.scoreValue}>{report.overall == null ? "—" : `${report.overall}%`}</div>
          <div className={s.scoreLabel}>Assessment score</div>
        </div>
      </div>

      <div className={`${s.modalBody} ${r.body}`}>
        {!sjt && (
          <Section title="Result">
            <p className={r.p}>
              {report.score} / {report.totalQuestions} correct · Integrity risk:{" "}
              <b style={{ textTransform: "capitalize" }}>{report.fakingRisk}</b>
            </p>
          </Section>
        )}

        {sjt && report.status === "pending" && (
          <div className={r.notice}>Scoring in progress. This usually takes a minute or two; reopen the result shortly.</div>
        )}
        {sjt && report.status === "review" && (
          <div className={r.notice}>
            This result is with an Awasource reviewer for manual scoring. You'll get the score here and on your webhook
            when it's done.
          </div>
        )}

        {sjt && scored && (
          <>
            {report.fit && (
              <div className={`${r.fit} ${FIT_CLASS[report.fit] || ""}`}>
                <div className={r.fitLabel}>{report.fit}</div>
                <div className={r.fitText}>{FIT_GUIDANCE[report.fit]}</div>
              </div>
            )}

            {(report.summary || report.benchmark) && (
              <Section title="Summary">
                {report.summary && <p className={r.p}>{report.summary}</p>}
                {report.benchmark && <p className={r.muted}>{benchmarkLine(report.benchmark)}</p>}
              </Section>
            )}

            {report.competencies.length > 0 && (
              <Section title="Competency breakdown">
                {(report.strengths.length > 0 || report.gaps.length > 0) && (
                  <div className={r.chips}>
                    {report.strengths.map((n) => (
                      <span key={`s-${n}`} className={`${r.chip} ${r.chipStrong}`}>
                        Strength · {n}
                      </span>
                    ))}
                    {report.gaps.map((n) => (
                      <span key={`g-${n}`} className={`${r.chip} ${r.chipGap}`}>
                        Development area · {n}
                      </span>
                    ))}
                  </div>
                )}
                <div className={r.compTools}>
                  <button type="button" className={r.linkBtn} onClick={toggleAll}>
                    {allOpen ? "Collapse all" : "Show evidence and answers"}
                  </button>
                </div>
                {report.competencies.map((c) => (
                  <CompetencyRow key={c.id} c={c} open={openIds.has(c.id)} onToggle={() => toggle(c.id)} />
                ))}
              </Section>
            )}

            <Section title="Suggested interview questions" icon={<IconMessageQuestion size={14} />}>
              {report.probes.length > 0 ? (
                <ol className={r.probes}>
                  {report.probes.map((p, i) => (
                    <li key={i} className={r.probe}>
                      <div className={r.probeQ}>{p.question}</div>
                      <div className={r.muted}>
                        {p.scenario ? <>For <b>{p.scenario}</b>. </> : null}
                        {p.listen_for ? <>Listen for: {p.listen_for}</> : null}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className={r.muted}>
                  {report.probesStatus === "none"
                    ? "Every competency was scored Strong, so there are no gaps to probe."
                    : report.probesStatus === "failed"
                      ? "Interview questions couldn't be prepared for this result."
                      : "Interview questions are being prepared — they'll appear here in a few seconds."}
                </p>
              )}
            </Section>
          </>
        )}

        {sjt && (
          <Section title="Integrity signals" icon={<IconShieldCheck size={14} />}>
            <p className={r.p}>{partnerIntegrityLines(report.signals).join(" · ") || "No flags recorded"}</p>
            {report.signals.aiSummary && <p className={r.muted}>{report.signals.aiSummary}</p>}
            <p className={r.muted}>
              For review only. These checks can be wrong and do not change the score. Leaving the page can be as simple as
              switching to another window or screen.
            </p>
          </Section>
        )}

        <Section title="How to read this report" icon={<IconInfoCircle size={14} />}>
          {sjt && scored && (
            <>
              <p className={r.muted}>{methodLine(report)}</p>
              <ul className={r.bandList}>
                {BAND_ORDER.map((b) => (
                  <li key={b}>
                    <span className={`${r.band} ${BAND_CLASS[b]}`}>{b}</span> {BAND_MEANING[b]}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className={r.oneInput}>{ONE_INPUT_NOTE}</p>
        </Section>

        <div className={s.modalActions}>
          <button type="button" className={s.btnOutline} onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className={`${s.btnOutline} ${r.downloadBtn}`}
            onClick={onDownload}
            disabled={!scored || downloading}
          >
            <IconDownload size={15} /> {downloading ? "Preparing PDF…" : "Download PDF"}
          </button>
        </div>
      </div>
    </>
  );
}
